import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import CodeMirror from "@uiw/react-codemirror";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { linter } from "@codemirror/lint";

import { BackendObj } from "../lib/backend";
import { classNames } from "../lib/utils";
import { selectIsSidebarLarge } from "../lib/scraper.slice";
import { IAdminExecuteDebugFunctionCallReq, IAdminExecuteDebugFunctionCallRes } from "../lib/backend/extractions.types.generated";

import { Button } from "../components/Button";
import { LoadingSpinnerLimit } from "../components/LoadingSpinner";
import { ArrowDownTrayIcon, PlayIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface DebugFunctionCallWindow {
    id: string;
    function_name: string;
    function_args: any;
    function_args_json: string;
    is_valid_json: boolean;
    result?: any;
    error?: string;
    is_loading: boolean;
}

export function AdminDebugObjectFunctionCall() {
    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const [windows, setWindows] = useState<DebugFunctionCallWindow[]>([]);
    const [is_drag_over, setIsDragOver] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);

        if (e.dataTransfer.files.length === 0) {
            setError("Please drop a JSON file containing debug objects.");
            return;
        }

        const file = e.dataTransfer.files[0];
        await processFile(file);
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const processFile = async (file: File) => {
        setError(undefined);

        if (!file.name.endsWith('.json')) {
            setError("Please select a JSON file.");
            return;
        }

        try {
            const text = await file.text();
            const debug_objects = JSON.parse(text);

            if (Array.isArray(debug_objects)) {
                // Multiple debug objects
                const new_windows: DebugFunctionCallWindow[] = debug_objects.map((obj, index) => {
                    const args_json = JSON.stringify(obj.function_args || {}, null, 2);
                    return {
                        id: `${Date.now()}-${index}`,
                        function_name: obj.function_name || '',
                        function_args: obj.function_args || {},
                        function_args_json: args_json,
                        is_valid_json: true,
                        is_loading: false
                    };
                });
                setWindows(prev => [...prev, ...new_windows]);
            } else if (debug_objects.function_name) {
                // Single debug object
                const args_json = JSON.stringify(debug_objects.function_args || {}, null, 2);
                const new_window: DebugFunctionCallWindow = {
                    id: `${Date.now()}`,
                    function_name: debug_objects.function_name,
                    function_args: debug_objects.function_args || {},
                    function_args_json: args_json,
                    is_valid_json: true,
                    is_loading: false
                };
                setWindows(prev => [...prev, new_window]);
            } else {
                setError("Invalid debug object format. Expected object with 'function_name' and 'function_args' properties.");
            }
        } catch (err) {
            setError("Invalid JSON file.");
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const executeDebugFunctionCall = async (windowId: string) => {
        const window = windows.find(w => w.id === windowId);
        if (!window) return;

        if (!window.is_valid_json) {
            setWindows(prev => prev.map(w =>
                w.id === windowId
                    ? { ...w, error: 'Please fix the invalid JSON before executing' }
                    : w
            ));
            return;
        }

        setWindows(prev => prev.map(w =>
            w.id === windowId
                ? { ...w, is_loading: true, error: undefined, result: undefined }
                : w
        ));

        try {
            // Parse the JSON from the editor
            const function_args = JSON.parse(window.function_args_json);

            const request: IAdminExecuteDebugFunctionCallReq = {
                function_name: window.function_name,
                function_args: function_args
            };

            const response: IAdminExecuteDebugFunctionCallRes = await BackendObj.extractions.adminExecuteDebugFunctionCall(request);

            setWindows(prev => prev.map(w =>
                w.id === windowId
                    ? { ...w, is_loading: false, result: response.result }
                    : w
            ));
        } catch (error) {
            setWindows(prev => prev.map(w =>
                w.id === windowId
                    ? { ...w, is_loading: false, error: error instanceof Error ? error.message : 'Unknown error' }
                    : w
            ));
        }
    };

    const removeWindow = (windowId: string) => {
        setWindows(prev => prev.filter(w => w.id !== windowId));
    };

    const clearAllWindows = () => {
        setWindows([]);
    };

    const updateFunctionArgs = (window_id: string, value: string) => {
        setWindows(prev => prev.map(w => {
            if (w.id === window_id) {
                // Validate JSON
                let is_valid = true;
                try {
                    JSON.parse(value);
                } catch (e) {
                    is_valid = false;
                }

                return {
                    ...w,
                    function_args_json: value,
                    is_valid_json: is_valid
                };
            }
            return w;
        }));
    };

    return (
        <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
            <div className="m-16 mb-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold leading-7 text-gray-600 sm:truncate sm:text-3xl sm:tracking-tight">
                        Debug Function Call Executor
                    </h2>
                    {windows.length > 0 && (
                        <Button
                            text="Clear All"
                            onClick={clearAllWindows}
                        />
                    )}
                </div>
                <div className="pt-5 border-b-4 border-sea_blue-700" />
            </div>

            <div className="mx-16 mb-6">
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                {windows.length === 0 && (
                    <div
                        className={classNames(
                            "flex justify-center items-center w-full h-96 border-2 border-dashed border-gray-300 rounded-lg p-5",
                            is_drag_over ? "bg-sea_blue-100" : ""
                        )}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <div className="text-center">
                            <ArrowDownTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-4 text-sm font-semibold text-gray-900">Drop JSON file here</h3>
                            <p className="mt-2 mb-8 text-sm text-gray-500">
                                Drop a JSON file containing an array of debug objects.
                            </p>
                            <Button text="Upload file" onClick={handleButtonClick} />
                        </div>
                    </div>
                )}

                {windows.length > 0 && (
                    <div className="space-y-6">
                        {windows.map((window) => (
                            <div key={window.id} className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        Function: {window.function_name}
                                    </h3>
                                    <button
                                        onClick={() => removeWindow(window.id)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Function Arguments</h4>
                                        <div className="w-full shadow border">
                                            <CodeMirror
                                                value={window.function_args_json}
                                                height="800px"
                                                theme="light"
                                                extensions={[json(), linter(jsonParseLinter())]}
                                                onChange={(value) => updateFunctionArgs(window.id, value)}
                                            />
                                        </div>
                                        <div className={`mt-1 text-sm ${window.is_valid_json ? "text-green-600" : "text-red-600"}`}>
                                            {window.is_valid_json ? "Valid JSON" : "Invalid JSON"}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="text-sm font-medium text-gray-700">Output</h4>
                                            <Button
                                                icon={PlayIcon}
                                                text="Run"
                                                onClick={() => executeDebugFunctionCall(window.id)}
                                                disabled={window.is_loading || !window.is_valid_json}
                                            />
                                        </div>
                                        <div className="bg-gray-50 rounded-md p-3 min-h-32">
                                            {window.is_loading && (
                                                <div className="flex items-center justify-center h-24">
                                                    <LoadingSpinnerLimit text="Executing..." />
                                                </div>
                                            )}
                                            {window.error && (
                                                <div className="text-red-600 text-sm">
                                                    Error: {window.error}
                                                </div>
                                            )}
                                            {window.result && !window.is_loading && (
                                                <pre className="text-sm text-gray-600 whitespace-pre-wrap">
                                                    {JSON.stringify(window.result, null, 2)}
                                                </pre>
                                            )}
                                            {!window.result && !window.error && !window.is_loading && (
                                                <div className="text-gray-400 text-sm italic">
                                                    Click "Run" to execute the function call
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <input
                type="file"
                className="hidden"
                ref={fileInputRef}
                accept=".json"
                onChange={handleFileChange}
            />
        </div>
    );
}